import * as _ from 'lodash';
import { Component } from 'react';
import { connect } from 'react-redux';
import Select from 'react-select-2';
import { createStructuredSelector } from 'reselect';
import type { Styles } from 'react-select-2/lib/styles';

import Locator from '../../../../../shared/Locator';
import { compactRevision } from '../../../../helpers/revisions';
import ProjectsModule from '../../../../redux/projects';
import { selectOptionFactory, trimString } from '../../../../utils';
import Icon, { icons } from '../../../generic/Icon';
import type { Revision } from '../../../../../shared/model-typings';
import type { SelectOption } from '../../../../types';

//  Because we pass `isMinimal` in the request, we only get the locator and message back.
//  This keeps the request relatively light, especially as we fetch 100 revisions at a time.
type MinimalRevision = Pick<Revision, 'locator' | 'message'>;

type MapStateProps = {
  revisions: Revision[];
  isFetchingRevision: boolean;
};

type DispatchProps = {
  fetchRevisions: (locator: string, branch: string) => Promise<unknown>;
  onChangeRevision: (revision: Revision) => void;
};

type ProjectReleaseCommitSelectProps = {
  locator: string;
  latestRevision: Revision;
  isEditable: boolean;
  branch: string;
  revision: MinimalRevision;
} & MapStateProps & DispatchProps;

const FALLBACK_TEXT = 'Select a revision';

const NO_OPTIONS_MESSAGE = 'This branch does not have any built revisions. Try a different branch';

//  Individual option for the revision dropdown
const ProjectReleaseRevisionSelectOption = (
  { value, label, revisionTag, latestRevision }:
  { value: string, label: string; revisionTag?: string, latestRevision?: Revision },
) => (
  <div>
    {revisionTag && (
      <span className="label label-rounded pill-component margin-right-xs">
        {value === latestRevision?.locator && (
          <span>(Latest) </span>
        )}
        {revisionTag}
      </span>
    )}
    <span>
      {trimString(label, 18)}
    </span>
  </div>
);

class ProjectReleaseRevisionSelect extends Component<ProjectReleaseCommitSelectProps> {
  async componentDidUpdate(prevProps: ProjectReleaseCommitSelectProps) {
    let shouldFetchRevisions = false;

    //  Re-fetch revisions if the branch has changed and there are no revisions for this branch.
    if (prevProps.branch !== this.props.branch) {
      shouldFetchRevisions = true;
    }

    //  Re-fetch revisions if the branch hasn't changed but the branch
    //  dropdown was used to select the current branch again
    if (prevProps.revision && !this.props.revision) {
      shouldFetchRevisions = true;
    }

    if (shouldFetchRevisions) {
      await this.fetchRevisionsOnce();
    }

    //  If there are revisions in the store, but we haven't selected one, let's select it now.
    const { revision, revisions } = this.props;
    if (!revision && revisions.length > 0) {
      this.selectFirstRevision();
    }
  }

  /**
   * Fetch this project's revisions only if they aren't already loaded.
   */
  async fetchRevisionsOnce() {
    const { branch, revisions, locator, fetchRevisions } = this.props;
    if (revisions.length === 0) {
      await fetchRevisions(locator, branch);
    }
  }

  revisionToOption = (revision: MinimalRevision) => {
    const { latestRevision } = this.props;
    return {
      ...selectOptionFactory(
        revision.locator,
        // try to present the user with the most meaningful revision label given the data available
        revision.message ?? new Locator(revision.locator).revision.value ?? revision.locator,
      ),
      //  Get the revision tag, if it exists
      revisionTag: this.getRevisionTag(revision),
      //  Include the latest revision for comparison
      latestRevision,
    };
  };

  get revisionOptions() {
    const { revision, revisions } = this.props;

    const options = revisions.map(this.revisionToOption);

    // if the selected revision hasn't been loaded yet, include it as an option until it shows up
    if (revision && !revisions.some((rev) => rev.locator === revision.locator)) {
      options.push(this.revisionToOption(revision));
    }

    return options;
  }

  get revisionText() {
    const { isEditable, revision } = this.props;

    const revisionHash = revision ? (
      <span className="commit-pill">{this.getRevisionTag(revision)}</span>
    ) : null;

    if (isEditable) {
      const selectedRevision = this.selectedRevision;
      return selectedRevision
        ? (
          <div>
            {revisionHash}
          </div>
          ) : FALLBACK_TEXT;
    }

    return (
      <div className="revision-label">
        {revisionHash}
      </div>
    );
  }

  get selectedRevision() {
    const { revision } = this.props;
    if (!revision) {
      return null;
    }

    return this.revisionOptions.find(b => b.value === revision.locator);
  }

  get selectStyles() {
    return {
      control: (styles: Styles) => ({
        ...styles,
        fontSize: '13px',
        whiteSpace: 'nowrap',
      }),
      option: (styles: Styles) => ({
        ...styles,
        fontSize: '13px',
        whiteSpace: 'nowrap',
      }),
    };
  }

  getRevisionTag = (revision: MinimalRevision) => compactRevision(new Locator(revision.locator));

  //  Takes the first revision in the store and sets that as the selected revision.
  selectFirstRevision = () => {
    const { revisions, onChangeRevision } = this.props;
    onChangeRevision(_.head(revisions) as Revision);
  };

  handleChange = (selectedRevision: SelectOption) => {
    const { onChangeRevision, revisions } = this.props;

    const revision = revisions.find(r => r.locator === selectedRevision.value);
    if (!revision) {
      return;
    }

    onChangeRevision(revision);
  };

  render() {
    const { isEditable, isFetchingRevision } = this.props;
    const { selectedRevision } = this;
    const commit = this.revisionText;
    return isEditable ? (
      <Select
        classNamePrefix="revision-select"
        noOptionsMessage={() => NO_OPTIONS_MESSAGE}
        placeholder={FALLBACK_TEXT}
        value={selectedRevision}
        options={this.revisionOptions}
        onChange={this.handleChange}
        onMenuOpen={() => this.fetchRevisionsOnce()}
        styles={this.selectStyles}
        isLoading={isFetchingRevision}
        getOptionLabel={ProjectReleaseRevisionSelectOption}
      />
    ) : (
      <div>
        {isFetchingRevision
          ? (<Icon icon={icons.circleO} hasSpin />)
          : (<div className={isEditable ? 'animated fadeInLeft' : ''}>{commit}</div>)}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  revisions: ProjectsModule.selectors.getRevisionsForProject,
  isFetchingRevision: ProjectsModule.selectors.getProjectIsFetchingRevisions,
});

const mapDispatchToProps = {
  fetchRevisions: ProjectsModule.actions.fetchRevisions,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProjectReleaseRevisionSelect);
